import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, formatApiErrorDetail } from "@/lib/api";
import { LOGO } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CompleteKYC() {
  const nav = useNavigate();
  const token = localStorage.getItem("aru_kyc_token") || localStorage.getItem("aru_token");
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState([]);
  const [f, setF] = useState({
    full_legal_name: "", whatsapp: "", pan: "", aadhar: "", address: "", city: "", state: "", pincode: "",
    gst_registered: false, gst_number: "", acc_holder: "", bank_name: "", account_number: "", ifsc: "", upi_id: "",
  });
  const [files, setFiles] = useState({});
  const [customVals, setCustomVals] = useState({});

  useEffect(() => {
    if (!token) { nav("/login"); return; }
    axios.get(`${API}/custom-fields?form=kyc`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCustom(r.data)).catch(() => {});
  }, [token, nav]);

  const upd = (k, v) => setF({ ...f, [k]: v });
  const setFile = (k, file) => setFiles({ ...files, [k]: file });

  const submit = async (e) => {
    e.preventDefault();
    if (!files.aadhar_doc || !files.pan_doc || !files.photo || !files.signature)
      return toast.error("Aadhar, PAN, Photo and Signature files are required");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, k === "gst_registered" ? String(v) : v));
      fd.append("custom", JSON.stringify(customVals));
      fd.append("aadhar_doc", files.aadhar_doc);
      fd.append("pan_doc", files.pan_doc);
      if (files.gst_doc) fd.append("gst_doc", files.gst_doc);
      fd.append("photo", files.photo);
      fd.append("signature", files.signature);
      await axios.post(`${API}/kyc`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("KYC submitted! Awaiting Super Admin approval.");
      if (localStorage.getItem("aru_kyc_token")) {
        localStorage.removeItem("aru_kyc_token");
        nav("/login");
      } else {
        window.location.href = "/";
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "KYC submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-background p-4">
      <Card className="mx-auto max-w-3xl p-8" data-testid="kyc-page">
        <img src={LOGO} alt="ARU" className="mx-auto mb-4 h-11 w-auto" />
        <h2 className="text-center font-heading text-2xl font-bold">Complete Your KYC</h2>
        <p className="mt-1 text-center text-sm text-muted-foreground">Required before Super Admin can approve your account.</p>
        <form onSubmit={submit} className="mt-6 space-y-6">
          <div>
            <h3 className="mb-2 font-heading font-semibold">Personal Details</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Full Legal Name</Label><Input value={f.full_legal_name} onChange={(e) => upd("full_legal_name", e.target.value)} required data-testid="kyc-name" className="mt-1" /></div>
              <div><Label>WhatsApp Number</Label><Input value={f.whatsapp} onChange={(e) => upd("whatsapp", e.target.value)} required data-testid="kyc-whatsapp" className="mt-1" /></div>
              <div><Label>PAN Number</Label><Input value={f.pan} onChange={(e) => upd("pan", e.target.value)} required data-testid="kyc-pan" className="mt-1" /></div>
              <div><Label>Aadhar Number</Label><Input value={f.aadhar} onChange={(e) => upd("aadhar", e.target.value)} required data-testid="kyc-aadhar" className="mt-1" /></div>
              <div className="sm:col-span-2"><Label>Complete Address</Label><Input value={f.address} onChange={(e) => upd("address", e.target.value)} required data-testid="kyc-address" className="mt-1" /></div>
              <div><Label>City</Label><Input value={f.city} onChange={(e) => upd("city", e.target.value)} required data-testid="kyc-city" className="mt-1" /></div>
              <div><Label>State</Label><Input value={f.state} onChange={(e) => upd("state", e.target.value)} required data-testid="kyc-state" className="mt-1" /></div>
              <div><Label>Pin Code</Label><Input value={f.pincode} onChange={(e) => upd("pincode", e.target.value)} required data-testid="kyc-pincode" className="mt-1" /></div>
            </div>
          </div>
          <div>
            <h3 className="mb-2 font-heading font-semibold">GST</h3>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.gst_registered} onCheckedChange={(v) => upd("gst_registered", v)} data-testid="kyc-gst-reg" />GST Registered?</label>
            {f.gst_registered && <div className="mt-2"><Label>GST Number</Label><Input value={f.gst_number} onChange={(e) => upd("gst_number", e.target.value)} data-testid="kyc-gst-number" className="mt-1" /></div>}
          </div>
          <div>
            <h3 className="mb-2 font-heading font-semibold">Bank Details</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Account Holder Name</Label><Input value={f.acc_holder} onChange={(e) => upd("acc_holder", e.target.value)} required data-testid="kyc-acc-holder" className="mt-1" /></div>
              <div><Label>Bank Name</Label><Input value={f.bank_name} onChange={(e) => upd("bank_name", e.target.value)} required data-testid="kyc-bank" className="mt-1" /></div>
              <div><Label>Account Number</Label><Input value={f.account_number} onChange={(e) => upd("account_number", e.target.value)} required data-testid="kyc-account" className="mt-1" /></div>
              <div><Label>IFSC Code</Label><Input value={f.ifsc} onChange={(e) => upd("ifsc", e.target.value)} required data-testid="kyc-ifsc" className="mt-1" /></div>
              <div><Label>UPI ID</Label><Input value={f.upi_id} onChange={(e) => upd("upi_id", e.target.value)} data-testid="kyc-upi" className="mt-1" /></div>
            </div>
          </div>
          <div>
            <h3 className="mb-2 font-heading font-semibold">Documents</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Aadhar Card (PDF, ≤1MB)</Label><Input type="file" accept=".pdf" onChange={(e) => setFile("aadhar_doc", e.target.files[0])} data-testid="kyc-aadhar-doc" className="mt-1" /></div>
              <div><Label>PAN Card (PDF, ≤1MB)</Label><Input type="file" accept=".pdf" onChange={(e) => setFile("pan_doc", e.target.files[0])} data-testid="kyc-pan-doc" className="mt-1" /></div>
              <div><Label>GST Certificate (PDF, ≤1MB, optional)</Label><Input type="file" accept=".pdf" onChange={(e) => setFile("gst_doc", e.target.files[0])} data-testid="kyc-gst-doc" className="mt-1" /></div>
              <div><Label>Photo (PDF/JPG)</Label><Input type="file" accept=".pdf,.jpg,.jpeg" onChange={(e) => setFile("photo", e.target.files[0])} data-testid="kyc-photo" className="mt-1" /></div>
              <div><Label>Signature (PDF/JPG)</Label><Input type="file" accept=".pdf,.jpg,.jpeg" onChange={(e) => setFile("signature", e.target.files[0])} data-testid="kyc-signature" className="mt-1" /></div>
            </div>
          </div>
          {custom.length > 0 && (
            <div>
              <h3 className="mb-2 font-heading font-semibold">Additional Information</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {custom.map((cf) => (
                  <div key={cf.id}>
                    <Label>{cf.label}{cf.required && " *"}</Label>
                    <Input value={customVals[cf.key] || ""} onChange={(e) => setCustomVals({ ...customVals, [cf.key]: e.target.value })} required={cf.required} data-testid={`kyc-custom-${cf.key}`} className="mt-1" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button type="submit" className="w-full" disabled={busy} data-testid="kyc-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Submit KYC
          </Button>
        </form>
      </Card>
    </div>
  );
}
